import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CardNotification, Text, Textarea } from "@foris/avocado-suite";
import Modal from "@components/modal";
import type { ReviewerOption } from "@services/types/assignment-proposal/assignmentProposalTypes";
import ReviewerSelect from "../reviewer-select";
import styles from "./confirmProposalModal.module.scss";

interface CommonProps {
  isSubmitting: boolean;
  /** Already translated message of the last failed attempt; cleared by the parent on retry. */
  errorMessage?: string | null;
  onCancel: () => void;
  onConfirm: (params: { reviewer: ReviewerOption | null; comment: string }) => void;
}

/**
 * - "send": the requester sends (or resends) the proposal to one intermediate reviewer; the
 *   validation issues pick the warning variant, and a resend preselects the previous reviewer.
 * - "pre-approve": the chosen intermediate reviewer gives the approval.
 * - "apply": a final reviewer applies the proposal, warned when that skips the intermediate step.
 */
export type ConfirmProposalModalProps = CommonProps &
  (
    | { kind: "send"; hasIssues: boolean; initialReviewer?: ReviewerOption | null }
    | { kind: "pre-approve" }
    | { kind: "apply"; skipsIntermediateReview: boolean }
  );

/**
 * Last step before a proposal moves on, with an optional comment. The parent mounts it only while it
 * is open, so cancelling resets the fields and a failed attempt keeps them.
 */
const ConfirmProposalModal = (props: ConfirmProposalModalProps) => {
  const { kind, isSubmitting, errorMessage, onCancel, onConfirm } = props;
  const { t } = useTranslation();
  const [reviewer, setReviewer] = useState<ReviewerOption | null>(
    props.kind === "send" ? (props.initialReviewer ?? null) : null,
  );
  const [comment, setComment] = useState("");
  const keyPrefix = `assignation-edit.proposal.${kind}-modal`;
  const hasIssues = props.kind === "send" && props.hasIssues;
  const canConfirm = (kind !== "send" || !!reviewer) && !isSubmitting;
  const descriptionKey =
    props.kind === "send"
      ? `${keyPrefix}.description.${props.hasIssues ? "issues" : "clean"}`
      : `${keyPrefix}.description`;

  return (
    <Modal
      typeState={hasIssues ? "warning" : "confirm"}
      show
      title={t(`${keyPrefix}.title`)}
      textButtonPrincipal={t(`${keyPrefix}.confirm`)}
      textButtonSecondary={t(`${keyPrefix}.cancel`)}
      isLoading={isSubmitting}
      isDisabledButtonPrincipal={!canConfirm}
      onClose={onCancel}
      onClickSecondary={onCancel}
      onClickPrincipal={() => {
        if (canConfirm) onConfirm({ reviewer, comment });
      }}
    >
      <div className={styles.confirmProposalModal}>
        <Text>{t(descriptionKey)}</Text>

        {props.kind === "apply" && props.skipsIntermediateReview && (
          <CardNotification state="warning" title={t(`${keyPrefix}.skips-intermediate`)} outlined />
        )}

        {props.kind === "send" && (
          <ReviewerSelect value={reviewer} disabled={isSubmitting} onChange={setReviewer} />
        )}

        <Textarea
          label={t(`${keyPrefix}.comment.label`)}
          placeholder={t(`${keyPrefix}.comment.placeholder`)}
          value={comment}
          disabled={isSubmitting}
          onChange={event => setComment(event.currentTarget.value)}
        />

        {!!errorMessage && (
          <div role="alert">
            <CardNotification state="error" title={errorMessage} outlined />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ConfirmProposalModal;
